import type { ISchedulerTask } from '../../interfaces/IScheduler.js';
import { remainingRequiredChecks } from './checklistGate.js';
import type { LadderRung } from './ladder.js';

// ---------------------------------------------------------------------------
// What a task still needs before it can be promised — the CLIENT's reading of
// the server's commit preconditions (2026-09-17 design, "Preconditions"),
// used to show the gaps up front rather than learn them from a 409. The
// server remains the judge; this only decides what the review asks for.
//
// And the task's STEP — the one action its place on the ladder calls for —
// with the hints that say what stands between the task and it. The web
// drawer and the mobile sheet read both from here, so they cannot disagree
// about which action a task is offered.
// ---------------------------------------------------------------------------

export type CommitGap = 'profession' | 'areas' | 'blocked';

/** The gaps of ONE leaf as it stands (or as a review's draft of it). */
export function commitGaps(
  task: Pick<ISchedulerTask, 'kind' | 'status' | 'profession' | 'locations'>,
): CommitGap[] {
  const gaps: CommitGap[] = [];
  if (task.status === 'blocked') gaps.push('blocked');
  if (task.kind === 'administrative') return gaps;
  if (!task.profession) gaps.push('profession');
  if ((task.locations?.length ?? 0) === 0) gaps.push('areas');
  return gaps;
}

/**
 * Whether a leaf still awaits its promise — what a parent's "Commit all"
 * counts and its review lists. A milestone is never promised (the server
 * refuses one, `commit-rules`), so no review can ever make it ready: counting
 * it kept a parent short of "all committed" for good, behind a Fix that
 * could not fix anything. A blocked leaf stays open — its review offers the
 * Unblock.
 */
export function isOpenLeaf(leaf: Pick<ISchedulerTask, 'committed' | 'status' | 'isMilestone'>): boolean {
  return !leaf.isMilestone && !leaf.committed && leaf.status !== 'done';
}

/** Whether a task is a parent — the cheap question behind "is this promised
 *  itself?", one pass with no index built. {@link leafDescendants} answers the
 *  same thing only as a side effect of walking to every leaf. */
export function hasChildren(tasks: readonly Pick<ISchedulerTask, 'parentId'>[], taskId: string): boolean {
  return tasks.some((task) => task.parentId === taskId);
}

/** Every LEAF under a task, in plan order — the rows a parent commit fans out
 *  to. Empty for a leaf itself. Iterative, like the server's walk. Builds a
 *  parent → children index over the whole plan, so memoize it at the call site
 *  (and ask {@link hasChildren} when only "is it a parent?" matters). */
export function leafDescendants(tasks: readonly ISchedulerTask[], rootId: string): ISchedulerTask[] {
  const childrenOf = new Map<string, ISchedulerTask[]>();
  for (const task of tasks) {
    if (task.parentId == null) continue;
    const list = childrenOf.get(task.parentId);
    if (list) list.push(task);
    else childrenOf.set(task.parentId, [task]);
  }
  const leaves: ISchedulerTask[] = [];
  const stack = [...(childrenOf.get(rootId) ?? [])];
  const seen = new Set<string>();
  while (stack.length > 0) {
    const task = stack.pop()!;
    if (seen.has(task.id)) continue;
    seen.add(task.id);
    const children = childrenOf.get(task.id);
    if (children && children.length > 0) stack.push(...children);
    else leaves.push(task);
  }
  const order = new Map(tasks.map((task, i) => [task.id, i] as const));
  return leaves.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
}

export type TaskStep =
  | { kind: 'commitAll'; open: number; ready: number }
  | { kind: 'allCommitted' }
  | { kind: 'milestone'; done: boolean }
  | { kind: 'reopen' }
  | { kind: 'unblock' }
  | { kind: 'commit'; gaps: number }
  | { kind: 'markDone'; suggest: boolean; remaining: number; canTakeBack: boolean };

/**
 * The task's step, from where it stands on the ladder and its leaf
 * descendants (none for a leaf — a parent's step is its leaves' commit).
 *
 * `suggest` ("every area is done — mark it done?") is read off the rung: a
 * committed task not yet done stands on Areas done exactly when Trusstor calls
 * its activity complete, which is the backend's own `suggestDone` rule
 * (committed-activities.service). Reading the rung means any surface holding
 * the ladder holds the suggestion — no second feed field to thread through.
 */
export function taskStepOf(task: ISchedulerTask, rung: LadderRung, leaves: readonly ISchedulerTask[] = []): TaskStep {
  if (leaves.length > 0) {
    const open = leaves.filter(isOpenLeaf);
    if (open.length === 0) return { kind: 'allCommitted' };
    return { kind: 'commitAll', open: open.length, ready: open.filter((leaf) => commitGaps(leaf).length === 0).length };
  }
  // A milestone is hit or not hit — a date, never a promise.
  if (task.isMilestone) return { kind: 'milestone', done: task.status === 'done' };
  if (task.status === 'done') return { kind: 'reopen' };
  // Blocked comes before committed: a blocked promise is unblocked, never marked done.
  if (task.status === 'blocked') return { kind: 'unblock' };
  if (!task.committed) return { kind: 'commit', gaps: commitGaps(task).length };
  // Committed: done is the exit, gated by the checklist like everywhere else.
  // Take back only while the promise is all there is — no work seen yet.
  return {
    kind: 'markDone',
    suggest: rung === 'areasDone',
    remaining: remainingRequiredChecks(task.checklist),
    canTakeBack: rung === 'committed',
  };
}

/** One line of what stands between a task and its step, as an i18n key and its
 *  interpolation; `good` when the line is the go-ahead rather than a gap. */
export interface IStepHint {
  key: string;
  params?: Record<string, number>;
  good?: boolean;
}

export function stepHintKeys(task: Pick<ISchedulerTask, 'locations'>, step: TaskStep): IStepHint[] {
  switch (step.kind) {
    case 'commitAll':
      return [{ key: 'scheduler.commit.readyOf', params: { ready: step.ready, total: step.open } }];
    case 'allCommitted':
      return [{ key: 'scheduler.commit.allCommitted' }];
    case 'milestone':
      return [{ key: 'scheduler.commit.milestone' }];
    case 'reopen':
      return [];
    case 'unblock':
      return [{ key: 'scheduler.commit.blockedHint' }];
    case 'commit':
      return [
        step.gaps === 0
          ? { key: 'scheduler.commit.ready', good: true }
          : { key: 'scheduler.commit.gapsHint', params: { count: step.gaps } },
      ];
    case 'markDone':
      return [
        ...(step.suggest
          ? [{ key: 'scheduler.commit.areasDoneHint', params: { count: task.locations?.length ?? 0 }, good: true }]
          : []),
        ...(step.remaining > 0
          ? [{ key: 'scheduler.panel.actions.requiredRemaining', params: { count: step.remaining } }]
          : []),
      ];
  }
}
